import qs from 'qs'
import { merge } from 'lodash'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '@SonarCX/store'

import {
  fetchReminders as fetchRemindersAPI,
  createReminder as createReminderAPI,
  updateReminder as updateReminderAPI,
  deleteReminder as deleteReminderAPI,
  getReminderssWithQuery
} from '@SonarCX/lib/api'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { parseDatetimeValue } from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/helpers/parse'

type ReminderState = {
  reminders: APIReminder[]
  loading: boolean
  loaded: boolean
  open: boolean
  selectedReminder: APIReminder | null
  mutateDashboardOnEdit: boolean
  mutateDashboard: boolean
}

const initialState: ReminderState = {
  reminders: [],
  loading: false,
  loaded: false,
  open: false,
  selectedReminder: null,
  mutateDashboard: false,
  mutateDashboardOnEdit: false
}

const reminderSlice = createSlice({
  name: 'reminder',
  initialState,
  reducers: {
    startLoad: (state: ReminderState) => {
      state.loading = true
    },

    finishLoad: (
      state: ReminderState,
      action: PayloadAction<APIReminder[]>
    ) => {
      state.loading = false
      state.loaded = true
      state.reminders = action.payload
    },

    createRem: (state: ReminderState, action: PayloadAction<APIReminder>) => {
      state.reminders.push(action.payload)
    },

    updateRem: (state: ReminderState, action: PayloadAction<APIReminder>) => {
      const { id } = action.payload
      const index = state.reminders.findIndex(reminder => reminder.id === id)
      state.reminders[index] = action.payload
    },

    deleteRem: (state: ReminderState, action: PayloadAction<string>) => {
      const index = state.reminders.findIndex(
        reminder => reminder.id === action.payload
      )
      state.reminders.splice(index, 1)
    },

    updateAllReminders: (
      state: ReminderState,
      action: PayloadAction<APIReminder[]>
    ) => {
      state.reminders = action.payload
      state.loading = false
      state.loaded = true
    },
    createNewReminder: (state: ReminderState) => {
      state.open = true
    },
    editReminder: (
      state: ReminderState,
      action: PayloadAction<APIReminder>
    ) => {
      state.open = true
      state.selectedReminder = action.payload
    },
    changeMutateDashboard: (
      state: ReminderState,
      action: PayloadAction<boolean>
    ) => {
      state.mutateDashboard = action.payload
    },
    changeMutateDashboardOnEdit: (
      state: ReminderState,
      action: PayloadAction<boolean>
    ) => {
      state.mutateDashboardOnEdit = action.payload
    },

    closeReminderModal: (state: ReminderState) => {
      state.open = false
      state.selectedReminder = null
      state.mutateDashboardOnEdit = false
    }
  }
})

export const {
  startLoad,
  finishLoad,
  createRem,
  updateRem,
  deleteRem,
  updateAllReminders,
  createNewReminder,
  editReminder,
  closeReminderModal,
  changeMutateDashboard,
  changeMutateDashboardOnEdit
} = reminderSlice.actions

export const getReminders = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(startLoad())
      const reminders = await fetchRemindersAPI()
      dispatch(finishLoad(reminders))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const createReminder = (data: ReminderPOST) => {
  return async (dispatch: AppDispatch) => {
    const reminder = await createReminderAPI(data)
    dispatch(createRem(reminder))
  }
}

export const updateReminder = (id: string, data: ReminderPOST) => {
  return async (dispatch: AppDispatch) => {
    const reminder = await updateReminderAPI(id, data)
    dispatch(updateRem(reminder))
  }
}

export const deleteReminder = (id: string) => {
  return async (dispatch: AppDispatch) => {
    await deleteReminderAPI(id)
    dispatch(deleteRem(id))
  }
}

export const loadRemindersByDateRange = (
  startOfMonth: string,
  endOfMonth: string
) => {
  return async (dispatch: AppDispatch) => {
    dispatch(startLoad())
    let params = {}

    params = merge(
      params,
      parseDatetimeValue(startOfMonth, 'active_between_start')
    )
    params = merge(params, parseDatetimeValue(endOfMonth, 'active_between_end'))
    let result = qs.stringify(params, { arrayFormat: 'brackets' })
    let page = 0
    const perPage = 100
    const getQuery = () =>
      `?${result}&include_expired=true&per_page=${perPage}&page=${page}`

    // Initial query to figure out total
    const {
      data,
      pagination: { total }
    } = await getReminderssWithQuery(getQuery())
    const fetchedReminders = [...data]
    // Get all remaining pages of tasks
    while ((page + 1) * perPage < total) {
      page += 1
      const { data } = await getReminderssWithQuery(getQuery())
      fetchedReminders.push(...data)
    }

    dispatch(batchAddReminders(fetchedReminders))
  }
}

export const batchAddReminders = (remindersToAdd: APIReminder[]) => {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { reminders } = selectReminder(getState())

    let existingSet = new Set(reminders.map(item => item.id))
    let nonRepeatedReminders = remindersToAdd.filter(
      item => !existingSet.has(item.id)
    )

    const newReminders = reminders.concat(nonRepeatedReminders)

    dispatch(updateAllReminders(newReminders))
  }
}

export const selectReminder = (state: RootState) => state.reminder

export default reminderSlice.reducer
